import { useEffect, useRef, useState } from "react";
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { loadDict } from "@/lib/poetry/dict";
import { checkParagraph, addMyWord } from "@/lib/spell";

const key = new PluginKey("liveCheck");

const charPositions = (node, pos) => {
  const idx = [];
  node.descendants((c, p) => { if (c.isText) for (let i = 0; i < c.text.length; i += 1) idx.push(pos + 1 + p + i); });
  return idx;
};

export const locate = (doc, fix, text) => {
  let hit = null;
  doc.descendants((node, pos) => {
    if (hit || !node.isTextblock) return !hit;
    if (node.textContent !== text) return false;
    const at = text.indexOf(fix.original);
    if (at >= 0) { const idx = charPositions(node, pos); hit = { from: idx[at], to: idx[at + fix.original.length - 1] + 1 }; }
    return false;
  });
  return hit;
};

export const ignoreKey = (f, text) => {
  const at = text.indexOf(f.original);
  const start = Math.max(text.lastIndexOf(".", at), text.lastIndexOf("!", at), text.lastIndexOf("?", at)) + 1;
  const ends = [".", "!", "?"].map((c) => text.indexOf(c, at + f.original.length)).filter((i) => i >= 0);
  return `${f.original}|${text.slice(start, ends.length ? Math.min(...ends) + 1 : text.length).trim()}`;
};

const build = (doc, store, prev) => {
  if (!store.enabled) return DecorationSet.empty;
  const decos = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    const text = node.textContent;
    const fixes = store.cache.get(text);
    if (fixes) {
      const idx = charPositions(node, pos);
      fixes.filter((f) => !store.ignored.has(ignoreKey(f, text))).forEach((f) => {
        const at = text.indexOf(f.original);
        if (at < 0) return;
        decos.push(Decoration.inline(idx[at], idx[at + f.original.length - 1] + 1, { class: `lf-issue lf-issue-${f.kind === "grammar" ? "grammar" : "spelling"}`, "data-fix": f.id }, { fix: f }));
      });
    } else if (prev) {
      prev.find(pos, pos + node.nodeSize).forEach((d) => { if (doc.textBetween(d.from, d.to) === d.spec.fix?.original) decos.push(d); });
    }
    return false;
  });
  return DecorationSet.create(doc, decos);
};

export const LiveCheck = Extension.create({
  name: "liveCheck",
  addOptions() { return { store: null }; },
  addProseMirrorPlugins() {
    const { store } = this.options;
    return [new Plugin({
      key,
      state: { init: (_, st) => build(st.doc, store), apply: (tr, old, _o, st) => (tr.docChanged || tr.getMeta(key) ? build(st.doc, store, old.map(tr.mapping, tr.doc)) : old.map(tr.mapping, tr.doc)) },
      props: { decorations: (st) => key.getState(st) },
    })];
  },
});

const blocks = (doc) => { const out = []; doc.descendants((n) => { if (n.isTextblock) { out.push(n.textContent); return false; } return true; }); return out; };

export const useLiveCheck = ({ cid, userId, enabled, names }) => {
  const store = useRef({ cache: new Map(), ignored: new Set(JSON.parse(localStorage.getItem(`lf-ignore-${cid}`) || "[]")), enabled, names: new Set() }).current;
  const ext = useRef(LiveCheck.configure({ store })).current;
  const [editor, setEditor] = useState(null);
  const [issue, setIssue] = useState(null);
  store.enabled = enabled;
  if (names && names !== store.names) { store.names = names; store.cache.clear(); }
  const refresh = () => editor && !editor.isDestroyed && editor.view.dispatch(editor.state.tr.setMeta(key, true));

  useEffect(() => { refresh(); if (editor) editor.view.dom.setAttribute("spellcheck", enabled ? "true" : "false"); }, [enabled, editor]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!editor || !enabled) return undefined;
    let timer, stop = false;
    const run = async () => {
      await loadDict().catch(() => {});
      const todo = [...new Set(blocks(editor.state.doc))].filter((t) => t.trim().length >= 3 && !store.cache.has(t));
      for (let i = 0; i < todo.length && !stop; i += 8) {
        todo.slice(i, i + 8).forEach((t) => store.cache.set(t, checkParagraph(t, store.names)));
        if (!editor.isDestroyed) refresh();
        await new Promise((r) => { setTimeout(r, 0); }); // eslint-disable-line no-await-in-loop
      }
    };
    run();
    const onUpdate = () => { clearTimeout(timer); timer = setTimeout(run, 800); };
    editor.on("update", onUpdate);
    const onClick = (e) => {
      const el = e.target.closest?.(".lf-issue");
      if (!el) return;
      const id = el.getAttribute("data-fix");
      const pos = editor.view.posAtDOM(el, 0);
      const d = key.getState(editor.state).find(pos, pos + 1).find((x) => x.spec.fix?.id === id);
      if (!d) return;
      const $p = editor.state.doc.resolve(d.from);
      const r = el.getBoundingClientRect();
      setIssue({ fix: d.spec.fix, text: $p.parent.textContent, x: r.left, y: r.bottom + 6 });
    };
    editor.view.dom.addEventListener("click", onClick);
    return () => { stop = true; clearTimeout(timer); editor.off("update", onUpdate); if (!editor.isDestroyed) editor.view.dom.removeEventListener("click", onClick); };
  }, [editor, enabled, cid, names]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = () => {
    const hit = locate(editor.state.doc, issue.fix, issue.text);
    if (hit) editor.chain().focus().insertContentAt(hit, issue.fix.suggestion).run();
    setIssue(null);
  };
  const ignore = () => {
    store.ignored.add(ignoreKey(issue.fix, issue.text));
    localStorage.setItem(`lf-ignore-${cid}`, JSON.stringify([...store.ignored].slice(-500)));
    refresh(); setIssue(null);
  };
  const learn = () => {
    const w = issue.fix.original.toLowerCase();
    addMyWord(w);
    store.cache.forEach((v, k) => store.cache.set(k, v.filter((f) => f.original.toLowerCase() !== w)));
    refresh(); setIssue(null);
  };
  return { ext, setEditor, issue, closeIssue: () => setIssue(null), apply, ignore, learn, userId };
};

export const liveCheckPref = (userId) => localStorage.getItem(`lf-livecheck-${userId}`) !== "off";
export const setLiveCheckPref = (userId, on) => localStorage.setItem(`lf-livecheck-${userId}`, on ? "on" : "off");

export const IssuePopover = ({ issue, onApply, onIgnore, onLearn, onClose }) => {
  useEffect(() => {
    const h = (e) => { if (!e.target.closest?.("[data-testid='live-issue-popover']") && !e.target.closest?.(".lf-issue")) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);
  const { fix } = issue;
  return (
    <div className="fixed z-50 w-72 rounded-md border border-[#2D3442] bg-[#0F131A] p-3 text-sm shadow-[0_10px_30px_rgba(0,0,0,0.5)]" style={{ left: Math.min(issue.x, window.innerWidth - 300), top: Math.min(issue.y, window.innerHeight - 160) }} data-testid="live-issue-popover">
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#F59E0B]">{fix.kind === "grammar" ? "Grammar" : "Spelling"}</div>
      <div className="mt-1 text-[#A8A090] line-through">{fix.original}</div>
      <div className="text-[#F3EBDD]" data-testid="live-issue-suggestion">{fix.suggestion}</div>
      {fix.reason && <div className="mt-1 text-xs text-[#A8A090]">{fix.reason}</div>}
      <div className="mt-3 flex gap-2">
        <button onClick={onApply} className="rounded bg-[#D97706] px-3 py-1 text-xs font-medium text-[#0B0D10] hover:bg-[#F59E0B]" data-testid="live-issue-apply">Apply</button>
        <button onClick={onIgnore} className="rounded border border-[#2D3442] px-3 py-1 text-xs text-[#D6CCBC] hover:border-[#D97706]" data-testid="live-issue-ignore">Ignore</button>
        {fix.kind === "spelling" && onLearn && <button onClick={onLearn} className="rounded border border-[#2D3442] px-3 py-1 text-xs text-[#D6CCBC] hover:border-[#D97706]" data-testid="live-issue-learn">Add to dictionary</button>}
      </div>
    </div>
  );
};
